<template>
  <FullscreenFrontageDialog ref="frontageDialog">
    <div class="files-uploader-dialog">

      <VcInputModal
        :show-dialog="showDialog"
        :useOffset="false"
        :title="$t('client.upload_documents_dialog.modal_title')"
        :okButtonLabel="$t('client.upload_documents_dialog.cta')"
        @onCloseButtonClicked="handleClose">
        <template v-slot:content>
          <form class="files-uploader-dialog__content">

            <section class="files-uploader-dialog__input-wrap">
              <div class="files-uploader-dialog__alert-wrap" v-if="someValidationsFailed">
                <VcAlert type="attention"
                         :header-text="$t('client.upload_documents_dialog.alert_title')"
                         :body-text="$t('client.upload_documents_dialog.alert_subtitle')"
                         :closeButton=false
                         :isInlineHeader=true
                         dataQa="files-uploader-dialog-alert" />
              </div>
            </section>
            <section class="files-uploader-dialog__input-wrap">
              <VcDropzone
                :title="fileInputCta"
                :subtitle="$t('client.upload_documents_dialog.file_input_cta_subtitle')"
                :supportedFileTypes="allowedTypes"
                :supportedFileSizeMb="allowedSize"
                :disabled="uploading || maxAmount"
                @onFilesValidated="handleValidatedFiles" />
            </section>

            <section class="files-uploader-dialog__input-wrap">
              <header class="files-uploader-dialog__input-header">
                <h4 class="files-uploader-dialog__input-title">
                  <span>{{ $t('client.upload_documents_dialog.list_title') }}</span>
                  <span class="files-uploader-dialog__files-count">{{ fileCount }}</span>
                </h4>
              </header>
              <div :class="{
                'files-list-wrap': true,
                'files-list-wrap--shake': shaking }"
                   @animationend="toggleShake(false)">
                <FilesList
                  :filesList="files"
                  :isMobile="isMobile"
                  @file-action-clicked="handleActionClicked"
                  @edit-item="handleSave"
                  @close-dialog="handleClose" />
              </div>
            </section>

          </form>
        </template>

        <template v-slot:footer>
          <VcModalFooter
            direction="horizontal"
            :buttons='[
              {label: $t("client.upload_documents_dialog.cta_secondary"), event: "onCancelClick", color: "white"},
              {label: $t("client.upload_documents_dialog.cta"), event: "onUploadClick", color: "secondary", loading: uploading},
              ]'
            @onUploadClick="handleSubmit"
            @onCancelClick="handleClose"
          />
        </template>

      </VcInputModal>
    </div>
  </FullscreenFrontageDialog>
</template>

<script>
import { FullscreenFrontageDialog } from "@/modules/common/components";
import FilesList from "@/modules/filesUploader/views/FilesList";
import { mapGetters } from "vuex";
import { postQuery } from "@/modules/common/utils";
import { v4 as uuidv4 } from 'uuid'
import { SUPPORTED_TYPES, MAX_FILE_AMOUNT, MAX_FILE_SIZE_MB, WILDCARD_MAX_FILE_SIZE_MB } from '@/modules/common/constants/fileUploadDefinitions';
import {showSuccess, showError} from "@/modules/common/services/frontageToastService.js";


export default {
  name: 'FilesUploaderDialog',
  components: {
    FullscreenFrontageDialog,
    FilesList
  },
  props: {
    uploadAsRequired: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['isMobile']),
    allowedTypes() {
      return this.$hasFeature('can_upload_videos') ? [] : SUPPORTED_TYPES;
    },
    allowedSize() {
      return this.$hasFeature('can_upload_videos') ? WILDCARD_MAX_FILE_SIZE_MB : MAX_FILE_SIZE_MB;
    },
    fileInputCta() {
      return this.isMobile
        ? this.$t('client.upload_documents_dialog.file_input_cta_mobile')
        : this.$t('client.upload_documents_dialog.file_input_cta_desktop')
    },
    fileCount() {
      return this.files.length ? `(${this.files.length})` : ''
    },
    maxAmount() {
      return this.files.length === MAX_FILE_AMOUNT;
    },
    someValidationsFailed() {
      return this.files.some(file => file.sizeValidated === false || file.typeValidated === false);
    },
  },
  data() {
    return {
      files: [],
      showDialog: true,
      uploading: false,
      shaking: false,
    }
  },
  methods: {
    toggleShake(value = true) {
      this.shaking = value;
    },
    handleValidatedFiles(e) {
      const { validatedFiles } = e;
      if (!validatedFiles) return;

      const enrichedFiles = validatedFiles.map(item => {
        const enrichedFile = {
          ...item,
          name: item.file.name,
          description: '',
          signatureRequired: this.uploadAsRequired,
          uid: uuidv4(),
        };

        if (item.sizeValidated === false) {
          enrichedFile.errorMessage = this.$t('client.upload_documents_dialog.validation_error_size');
        }

        if (item.typeValidated === false) {
          enrichedFile.errorMessage = this.$t('client.upload_documents_dialog.validation_error_type');
        }

        return enrichedFile;
      });

      for (const enrichedFile of enrichedFiles) {
        if (this.maxAmount) return;
        else this.files.push(enrichedFile);
      }
    },
    handleClose() {
      this.showDialog = false;
      this.$refs.frontageDialog.handleClose();
    },
    async handleSubmit() {
      if (this.someValidationsFailed || !this.files.length) return this.toggleShake();
      const statusesCount = { success: 0, failure: 0 };
      this.uploading = true;
      const formDatas = this.files.map(item => {
        const formData = new FormData();
        formData.append('files[0][file]', item.file);
        formData.append('files[0][title]', item.name);
        formData.append('files[0][description]', item.description);
        formData.append('files[0][signature_required]', item.signatureRequired);
        formData.append('files[0][template]', true);
        return formData;
      });
      const promises = formDatas.map(formData => postQuery('documents', formData));
      await Promise.allSettled(promises).then((results) => results.forEach((result) => {
          if (result.status === "rejected") statusesCount.failure += 1
          else statusesCount.success += 1
      }));
      this.uploading = false;
      this.$frontageApi.send('refresh-document-list');
      const { success, failure } = statusesCount;
      this.showUploadStatus({ success, failure });
      this.handleClose();
    },
    showUploadStatus({ success, failure }) {
      if (failure) showError(`${failure} ${this.$t(`client.upload_documents_dialog.error_message${failure > 1 ? '_multiple' : ''}`)}`)
      else showSuccess(this.$t('client.upload_documents_dialog.success_message'))
    },
    handleActionClicked(action, fileUid) {
      if (action ==='Delete') this.handleDelete(fileUid);
    },
    handleSave(fileUid, item) {
      const { fileTitle, fileDescription } = item;
      this.files = this.files.map(file => file.uid === fileUid ? {
        ...file,
        name: fileTitle,
        description: fileDescription
      } : file);
    },
    async handleDelete(fileUid) {
      this.files = this.files.filter(file => file.uid !== fileUid);
    }
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.files-uploader-dialog {

  &__input-wrap {
    display: block;
    width: 100%;
    height: auto;

    &:not(:last-of-type) {
      margin-bottom: 12px;
    }
  }

  &__input-title {
    font-size: 13px;
    font-weight: 600;
  }

  &__files-count {
    margin-inline-start: 4px;
  }

  &__input-header {
    margin-bottom: 12px;
  }

  &__alert-wrap {
    margin-bottom: 24px;
  }
}

.files-list-wrap {
  transform: translate3d(0, 0, 0);
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 100%;
  max-height: calc(100vh - 300px);
  border: 1px solid $input-border;
  border-radius: 4px;

  @include md-and-up {
    max-height: 420px;
  }

  &--shake {
    animation: shakes 0.85s cubic-bezier(.36,.07,.19,.97) both;
  }
}

@keyframes shakes {
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }

  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
}

</style>
